import React, { Fragment, PropsWithChildren, useMemo } from 'react';
import { Box, List, Typography } from '@mui/material';
import { HeaderPrimaryButton, PageHeader } from 'components/layout/PageHeader';
import { ListSkeleton } from 'components/skeletons';
import { EmptyState } from 'components/empty.state';
import { DomainOutlined, TouchAppOutlined } from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetAgencies } from 'core/agencies/hooks';
import { ListCard } from 'components/list/list.card';

const menuSidebarWidth: string = "24rem";

const IndexPage = ({children}: PropsWithChildren) => {
    const {agencyId} = useParams();
    const {data, isLoading, isError} = useGetAgencies({onError: (err) => toast.error("Une erreur est survenue.")});

    const {openAgencies, closedAgencies} = useMemo(() => ({
        openAgencies: (data || []).filter((agency: any) => !agency.closed),
        closedAgencies: (data || []).filter((agency: any) => agency.closed),
    }), [data]);

    if (!isLoading && (isError || data === undefined)) { return (<Typography color={"red"}>Une erreur est survenue.</Typography>); }

    return (
        <Fragment>
            <PageHeader
                title='Agences'
                actions={<HeaderPrimaryButton component={Link} to='/dashboard/agencies/new'>Ajouter une agence</HeaderPrimaryButton>}
            />
            <Box sx={{display: 'flex', gap: 0, justifyContent: 'space-between'}}>
                <List sx={{width: menuSidebarWidth, flexShrink: 0, p: '1rem', height: 'calc(100vh - 72px)', overflowY: 'scroll'}} disablePadding>
                    {isLoading && <ListSkeleton withAvatar={false} withChips={false} />}
                    {!isLoading && openAgencies.length === 0 && (
                        <EmptyState
                            icon={<DomainOutlined />}
                            title={closedAgencies.length > 0 ? 'Aucune agence ouverte' : 'Aucune agence'}
                            description={closedAgencies.length > 0 ? 'Toutes vos agences sont fermées. Rouvrez-en une ou créez-en une nouvelle.' : 'Créez votre première agence pour y rattacher des agents et des missions.'}
                            action={<HeaderPrimaryButton component={Link} to='/dashboard/agencies/new' sx={{ boxShadow: 'none' }}>Ajouter une agence</HeaderPrimaryButton>}
                        />
                    )}
                    {openAgencies.map((agency: any) =>
                        <ListCard
                            key={agency.id}
                            component={Link}
                            to={`/dashboard/agencies/${agency.id}`}
                            title={agency.name}
                            selected={agencyId === agency.id.toString()}
                        />
                    )}
                    {closedAgencies.length > 0 && (
                        <Fragment>
                            <Typography component='li' sx={{ px: 0.5, pt: 2, pb: 1, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', listStyle: 'none' }}>
                                Agences fermées ({closedAgencies.length})
                            </Typography>
                            {closedAgencies.map((agency: any) =>
                                <Box key={agency.id} sx={{ opacity: 0.7 }}>
                                    <ListCard
                                        component={Link}
                                        to={`/dashboard/agencies/${agency.id}`}
                                        title={agency.name}
                                        selected={agencyId === agency.id.toString()}
                                        chips={[{label: 'Fermée', color: 'error'}]}
                                    />
                                </Box>
                            )}
                        </Fragment>
                    )}
                </List>
                <Box sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, pr: '1rem', py: '1rem', height: 'calc(100vh - 72px)', overflowY: 'scroll' }}>
                    {children ? (
                        <Box sx={{ borderRadius: '1rem', border: '1px solid #EDF0F3', boxShadow: 1, backgroundColor: 'background.paper', minWidth: 0, overflow: 'hidden' }}>
                            {children}
                        </Box>
                    ) : (
                        <EmptyState
                            size='md'
                            variant='dashed'
                            icon={<TouchAppOutlined />}
                            title="Sélectionnez une agence"
                            description="Cliquez sur une agence pour modifier ses informations."
                            sx={{ minHeight: 'calc(100vh - 72px - 2rem)' }}
                        />
                    )}
                </Box>
            </Box>
        </Fragment>
    );
};

export { IndexPage };
